// pdfGenerator.js
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const PDF_BLOCK_SELECTOR = '[data-pdf-block="true"]';

const getBlockBoundariesInCanvas = (element, canvasWidth, canvasHeight) => {
	const elementWidth = element.scrollWidth || element.offsetWidth || 1;
	const elementHeight = element.scrollHeight || element.offsetHeight || 1;
	const scaleX = canvasWidth / elementWidth;
	const scaleY = canvasHeight / elementHeight;
	const elementRect = element.getBoundingClientRect();

	const blocks = Array.from(element.querySelectorAll(PDF_BLOCK_SELECTOR));

	return blocks
		.map((block) => {
			const blockRect = block.getBoundingClientRect();
			const relativeTop =
				blockRect.top - elementRect.top + element.scrollTop;
			const relativeLeft =
				blockRect.left - elementRect.left + element.scrollLeft;
			const relativeBottom = relativeTop + blockRect.height;

			return {
				top: Math.max(0, Math.floor(relativeTop * scaleY)),
				bottom: Math.min(
					canvasHeight,
					Math.ceil(relativeBottom * scaleY),
				),
				left: Math.max(0, Math.floor(relativeLeft * scaleX)),
				height: Math.ceil(blockRect.height * scaleY),
			};
		})
		.filter((block) => block.bottom > block.top)
		.sort((a, b) => {
			if (a.top !== b.top) return a.top - b.top;
			return a.left - b.left;
		});
};

const getSafeSliceHeight = ({
	renderedHeight,
	canvasHeight,
	pageHeightPx,
	blockBoundaries,
}) => {
	const remainingHeight = canvasHeight - renderedHeight;
	if (remainingHeight <= pageHeightPx) {
		return remainingHeight;
	}

	const preferredEnd = renderedHeight + pageHeightPx - 2;
	const MIN_SLICE_HEIGHT = 1;
	const crossingBlocks = blockBoundaries
		.filter(
			(block) =>
				block.top < preferredEnd &&
				block.bottom > preferredEnd &&
				block.top > renderedHeight + MIN_SLICE_HEIGHT &&
				block.height <= pageHeightPx,
		)
		.sort((a, b) => {
			if (a.top !== b.top) return a.top - b.top;
			return a.left - b.left;
		});

	if (crossingBlocks.length > 0) {
		const bestBoundary = crossingBlocks[0].top - renderedHeight;
		if (bestBoundary > MIN_SLICE_HEIGHT) {
			return bestBoundary;
		}
	}

	return Math.min(pageHeightPx, remainingHeight);
};

/**
 * Gera um PDF a partir de 1 ou mais refs.
 * - Cada ref é tratado como um "bloco" independente.
 * - Cada bloco pode ocupar várias páginas.
 * - Blocos nunca são compactados na mesma página.
 *
 * @param {React.RefObject|React.RefObject[]} refOrRefs
 * @param {string} [fileName='notificacao.pdf']
 */
export const generatePDF = async (
	refOrRefs,
	fileName = 'notificacao.pdf',
	options = {},
) => {
	const refs = Array.isArray(refOrRefs) ? refOrRefs : [refOrRefs];
	const elements = refs.map((r) => r && r.current).filter((el) => !!el);
	const { scale = 2, imageQuality = 0.9 } = options;

	if (!elements.length) {
		console.error('Nenhum elemento de referência válido para gerar o PDF.');
		return;
	}

	try {
		const pdf = new jsPDF({
			orientation: 'portrait',
			unit: 'px',
			format: 'a4',
		});

		const pdfWidth = pdf.internal.pageSize.getWidth();
		const pdfHeight = pdf.internal.pageSize.getHeight();

		let isFirstPage = true;

		for (const element of elements) {
			// captura apenas este componente
			const canvas = await html2canvas(element, {
				scale,
				useCORS: true,
				logging: false,
			});

			const canvasWidth = canvas.width;
			const canvasHeight = canvas.height;
			const blockBoundaries = getBlockBoundariesInCanvas(
				element,
				canvasWidth,
				canvasHeight,
			);

			// altura em pixels do canvas que corresponde a UMA página PDF
			const pageHeightPx = (canvasWidth * pdfHeight) / pdfWidth;

			const pageCanvas = document.createElement('canvas');
			const pageCtx = pageCanvas.getContext('2d');
			const PAGE_MARGIN_TOP_PX = Math.round(canvasWidth * 0.025);

			let renderedHeight = 0;

			while (renderedHeight < canvasHeight) {
				const topMargin = isFirstPage ? 0 : PAGE_MARGIN_TOP_PX;
				const sliceHeight = getSafeSliceHeight({
					renderedHeight,
					canvasHeight,
					pageHeightPx: pageHeightPx - topMargin,
					blockBoundaries,
				});

				pageCanvas.width = canvasWidth;
				pageCanvas.height = sliceHeight + topMargin;

				pageCtx.clearRect(0, 0, pageCanvas.width, pageCanvas.height);
				pageCtx.fillStyle = '#ffffff';
				pageCtx.fillRect(0, 0, canvasWidth, topMargin);
				pageCtx.drawImage(
					canvas,
					0,
					renderedHeight,
					canvasWidth,
					sliceHeight,
					0,
					topMargin,
					canvasWidth,
					sliceHeight,
				);

				const imgData = pageCanvas.toDataURL(
					'image/jpeg',
					imageQuality,
				);
				const imgHeight =
					((sliceHeight + topMargin) * pdfWidth) / canvasWidth;

				if (!isFirstPage) {
					pdf.addPage();
				}

				pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, imgHeight);
				isFirstPage = false;
				renderedHeight += sliceHeight;
			}
		}

		pdf.save(fileName);
	} catch (error) {
		console.error('Erro ao gerar PDF:', error);
	}
};
